import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { EMAIL, PHONE_DISPLAY } from "@/lib/constants";

const Section = ({ title, testid, children }) => (
  <Reveal>
    <div data-testid={testid} className="border-b border-[#EDEAE3] py-8 first:pt-0 last:border-none">
      <h2 className="font-display text-xl font-medium text-navy sm:text-2xl">{title}</h2>
      <div className="mt-4 space-y-3 text-sm leading-relaxed text-stone-500 sm:text-base">{children}</div>
    </div>
  </Reveal>
);

export default function PolitiqueConfidentialitePage() {
  return (
    <PageShell>
      <PageHero
        testid="page-privacy-hero"
        label="Vos données"
        title="Politique de confidentialité"
        sub="Comment PME CozyHome collecte, utilise et protège vos données personnelles."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="pb-6 text-xs text-stone-400">Dernière mise à jour : septembre 2026</p>

          <Section title="Responsable du traitement" testid="privacy-controller">
            <p>
              Le responsable du traitement des données personnelles collectées sur ce site est <b className="text-navy">PME COZYHOME</b>,
              SASU au capital de 150 €, dont le siège social est situé 47 rue Vivienne, 75002 Paris (SIREN 103 556 882).
            </p>
            <p>Vous pouvez nous contacter à l'adresse {EMAIL} ou au {PHONE_DISPLAY}.</p>
          </Section>

          <Section title="Données collectées" testid="privacy-data">
            <p>Selon le formulaire que vous utilisez, nous pouvons collecter :</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Formulaire « Parler de mon projet » : nom, téléphone, e-mail, informations sur le projet (type, budget, surface, délai), code postal, commune, avancement, besoins et message ;</li>
              <li>Pièces jointes éventuellement transmises avec ce formulaire : photos, plans, devis ;</li>
              <li>Formulaire « Être rappelé » : nom, téléphone, code postal et motif de la demande ;</li>
              <li>Candidature Partenaire : informations professionnelles et de contact nécessaires à l'étude de la candidature (société, SIRET, coordonnées, etc.) ;</li>
              <li>Données techniques nécessaires à la sécurité et au fonctionnement du site (par exemple les cookies d'authentification pour les espaces privés — voir la page « Gestion des cookies »).</li>
            </ul>
          </Section>

          <Section title="Finalités" testid="privacy-purposes">
            <p>Ces données sont utilisées pour :</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>répondre à vos demandes ;</li>
              <li>rappeler les prospects qui le demandent ;</li>
              <li>étudier et gérer vos projets CozyHome ;</li>
              <li>traiter les candidatures partenaires ;</li>
              <li>gérer les comptes et espaces privés (Client, Partenaire, Entreprise) ;</li>
              <li>assurer la sécurité et le fonctionnement du service ;</li>
              <li>mesurer l'utilisation du site, uniquement lorsque votre consentement est requis et obtenu.</li>
            </ul>
          </Section>

          <Section title="Bases légales" testid="privacy-legal-basis">
            <ul className="list-disc space-y-1.5 pl-5">
              <li>L'exécution de mesures précontractuelles ou contractuelles, lorsque vous nous demandez un devis, un rappel ou un accompagnement de projet ;</li>
              <li>Notre intérêt légitime, pour la sécurité et le bon fonctionnement du site ;</li>
              <li>Votre consentement, pour les outils de mesure d'audience soumis à consentement ;</li>
              <li>Le cas échéant, le respect d'une obligation légale.</li>
            </ul>
          </Section>

          <Section title="Destinataires" testid="privacy-recipients">
            <p>
              Vos données sont destinées aux équipes CozyHome habilitées, et, lorsque cela est nécessaire à votre demande,
              aux partenaires ou entreprises concernés par votre projet. Elles ne sont ni vendues, ni cédées à des tiers à des fins commerciales.
            </p>
          </Section>

          <Section title="Prestataires techniques" testid="privacy-processors">
            <p>
              Certaines données peuvent être traitées par les prestataires techniques nécessaires au fonctionnement du service, notamment :
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>l'hébergement du site et de la base de données, assuré par l'infrastructure Emergent ;</li>
              <li>le stockage des fichiers et pièces jointes (photos, plans, devis), assuré par le service de stockage objet Emergent ;</li>
              <li>l'envoi des e-mails de notification, assuré par le service d'e-mail Emergent.</li>
            </ul>
          </Section>

          <Section title="Durées de conservation" testid="privacy-retention">
            <p>
              CozyHome conserve les données personnelles uniquement pendant la durée nécessaire aux finalités pour
              lesquelles elles ont été collectées.
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Demandes de contact, « Parler de mon projet » et « Être rappelé » sans contrat conclu : maximum 3 ans à compter de la collecte ou du dernier contact émanant du prospect.</li>
              <li>Pièces jointes fournies par un prospect (photos, plans, devis) : même durée que le dossier prospect auquel elles sont rattachées, sauf nécessité particulière ou demande de suppression applicable.</li>
              <li>Clients et projets ayant donné lieu à un contrat : pendant toute la durée de la relation contractuelle. À son terme, seules les données nécessaires sont archivées pendant les durées légales applicables.</li>
              <li>Documents comptables et pièces justificatives devant être conservés légalement : 10 ans lorsque cette obligation s'applique.</li>
              <li>Candidatures Partenaire n'ayant pas abouti : maximum 2 ans après le dernier contact, sauf demande d'effacement antérieure.</li>
              <li>Comptes des espaces privés : pendant la durée d'utilisation du compte ; les comptes durablement inactifs feront l'objet d'une suppression ou d'une anonymisation selon la politique interne applicable.</li>
              <li>Demandes d'exercice des droits RGPD : conservation limitée à la durée nécessaire au traitement de la demande et aux obligations de preuve applicables.</li>
            </ul>
            <p>
              Certaines données peuvent être archivées plus longtemps lorsqu'une obligation légale ou la nécessité de
              constater, exercer ou défendre un droit en justice l'impose.
            </p>
          </Section>

          <Section title="Sécurité" testid="privacy-security">
            <p>
              L'accès aux espaces privés (Client, Partenaire, Entreprise, Centrale) est protégé par une authentification
              sécurisée (mots de passe chiffrés, cookies de session marqués « httpOnly » et « Secure ») et un accès limité
              selon le rôle de chaque utilisateur. Les échanges avec le site sont chiffrés (HTTPS).
            </p>
          </Section>

          <Section title="Cookies et mesure d'audience" testid="privacy-cookies-audience">
            <p>
              Le site dépose des cookies strictement nécessaires à l'authentification des espaces privés. Un outil de mesure
              d'audience est par ailleurs actuellement fourni par l'infrastructure technique du site. Le détail complet est
              disponible sur notre page{" "}
              <a href="/cookies" className="font-semibold text-cognac hover:underline">Gestion des cookies</a>.
            </p>
          </Section>

          <Section title="Droits des personnes" testid="privacy-rights">
            <p>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, d'opposition, de limitation du traitement et de portabilité de vos données.</p>
          </Section>

          <Section title="Comment exercer ses droits" testid="privacy-exercise-rights">
            <p>Vous pouvez exercer ces droits en nous écrivant à {EMAIL}, en précisant votre demande et en joignant, si nécessaire, un justificatif d'identité.</p>
          </Section>

          <Section title="Réclamation auprès de la CNIL" testid="privacy-cnil">
            <p>
              Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez adresser une
              réclamation à la CNIL (Commission Nationale de l'Informatique et des Libertés) — 3 Place de Fontenoy,
              TSA 80715, 75334 Paris Cedex 07, ou via <span className="font-semibold text-navy">www.cnil.fr</span>.
            </p>
          </Section>

          <Section title="Mise à jour de la politique" testid="privacy-updates">
            <p>Cette politique de confidentialité pourra être mise à jour pour refléter toute évolution du site ou de la réglementation. Sa date de dernière mise à jour figure en haut de cette page.</p>
          </Section>
        </div>
      </section>
    </PageShell>
  );
}
